import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import {
  ClockIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  GiftIcon,
  BanknotesIcon,
  LinkIcon,
  FingerPrintIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  ChartPieIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import SEOMeta from "@/templates/SEOMeta";
import {SeoInternalLinks} from "@/components/SeoInternalLinks";
import { getI18nProps } from '@/utils/i18n';
import AffiliateAmbassadorSection from '@/components/AffiliateAmbassadorSection';

export default function AffiliateProgram({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('affiliateProgram');

  const stats = [
    {
      value: t('stats.commissionRate.value'),
      label: t('stats.commissionRate.label'),
      icon: CurrencyDollarIcon
    },
    {
      value: t('stats.cookieWindow.value'),
      label: t('stats.cookieWindow.label'),
      icon: ClockIcon
    },
    {
      value: t('stats.commissionPeriod.value'),
      label: t('stats.commissionPeriod.label'),
      icon: ArrowPathIcon
    }
  ];

  const features = [
    {
      icon: ClockIcon,
      title: t('features.cookieWindow.title'),
      text: t('features.cookieWindow.text')
    },
    {
      icon: CurrencyDollarIcon,
      title: t('features.commission.title'),
      text: t('features.commission.text')
    },
    {
      icon: ArrowPathIcon,
      title: t('features.commissionPeriod.title'),
      text: t('features.commissionPeriod.text')
    },
    {
      icon: ChartBarIcon,
      title: t('features.analytics.title'),
      text: t('features.analytics.text')
    },
    {
      icon: GiftIcon,
      title: t('features.promotional.title'),
      text: t('features.promotional.text')
    },
    {
      icon: BanknotesIcon,
      title: t('features.payouts.title'),
      text: t('features.payouts.text')
    }
  ];

  const processSteps = [
    {
      icon: LinkIcon,
      title: t('process.shareLink.title'),
      description: t('process.shareLink.description')
    },
    {
      icon: FingerPrintIcon,
      title: t('process.tracking.title'),
      description: t('process.tracking.description')
    },
    {
      icon: DevicePhoneMobileIcon,
      title: t('process.visitorExplores.title'),
      description: t('process.visitorExplores.description')
    },
    {
      icon: ShoppingCartIcon,
      title: t('process.purchase.title'),
      description: t('process.purchase.description')
    },
    {
      icon: CreditCardIcon,
      title: t('process.commission.title'),
      description: t('process.commission.description')
    },
    {
      icon: ChartPieIcon,
      title: t('process.performance.title'),
      description: t('process.performance.description')
    }
  ];

  const commissionTiers = [
    {
      name: t('programDetails.tiers.starter.name'),
      threshold: t('programDetails.tiers.starter.threshold'),
      commission: t('programDetails.tiers.starter.commission'),
      perks: t('programDetails.tiers.starter.perks')
    },
    {
      name: t('programDetails.tiers.ambassador.name'),
      threshold: t('programDetails.tiers.ambassador.threshold'),
      commission: t('programDetails.tiers.ambassador.commission'),
      perks: t('programDetails.tiers.ambassador.perks')
    }
  ];

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            img="/og.png"
            url={seoMeta?.url || "/affiliate-program"}
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero Section */}
          <section className="pt-28 pb-24 px-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-5">
              <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute top-20 right-10 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
              <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center space-y-8"
              >
                <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 100 }}
                >
                  <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                    {t('hero.title')}
                  </h1>
                </motion.div>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {t('hero.subtitle')}
                </p>
                <motion.div
                    className="pt-6"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                  <Button
                      onClick={() => window.open(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/invite/afivio`, '_blank')}
                      className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {t('hero.cta')}
                  </Button>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {stats.map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ y: -5 }}
                        className="text-center p-6 border rounded-xl bg-gradient-to-b from-white to-gray-50 hover:shadow-md transition-all duration-300"
                    >
                      <stat.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                        {stat.value}
                      </div>
                      <div className="text-gray-600 mt-2">{stat.label}</div>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-24 bg-gray-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
              <div className="text-center mb-16">
                <motion.h2
                    className="text-4xl font-bold mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                >
                  {t('process.title')}
                </motion.h2>
                <motion.p
                    className="text-gray-600 max-w-xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                  {t('process.subtitle')}
                </motion.p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {processSteps.map((step, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ y: -5, scale: 1.02 }}
                        className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                    >
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-4">
                          <step.icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-semibold">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 pl-14">{step.description}</p>
                      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <motion.h2
                    className="text-4xl font-bold mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                >
                  {t('programDetails.title')}
                </motion.h2>
                <motion.p
                    className="text-gray-600 max-w-xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                  {t('programDetails.subtitle')}
                </motion.p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {features.map((feature, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        whileHover={{ y: -10 }}
                        className="p-8 bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group"
                    >
                      <feature.icon className="w-12 h-12 text-blue-600 mb-6 group-hover:scale-110 transition-transform duration-300" />
                      <h3 className="text-2xl font-semibold mb-4">{feature.title}</h3>
                      <p className="text-gray-600">{feature.text}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Additional Info */}
          <section className="py-24 bg-gray-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-20 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
            </div>

            <div className="max-w-4xl mx-auto px-6 relative z-10">
              <div className="text-center mb-16">
                <motion.h2
                    className="text-4xl font-bold mb-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                >
                  {t('programDetails.title')}
                </motion.h2>
                <motion.p
                    className="text-gray-600 max-w-xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                >
                  {t('programDetails.subtitle')}
                </motion.p>
              </div>

              <div className="space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <h3 className="text-2xl font-semibold mb-4 flex items-center">
                    <CheckBadgeIcon className="w-8 h-8 text-green-500 mr-3" />
                    {t('programDetails.commissionStructure.title')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('programDetails.commissionStructure.text')}
                  </p>
                  <div className="overflow-hidden rounded-2xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-left">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-sm font-semibold text-gray-900">{t('programDetails.table.tier')}</th>
                          <th className="px-4 py-3 text-sm font-semibold text-gray-900">{t('programDetails.table.threshold')}</th>
                          <th className="px-4 py-3 text-sm font-semibold text-gray-900">{t('programDetails.table.commission')}</th>
                          <th className="px-4 py-3 text-sm font-semibold text-gray-900">{t('programDetails.table.perks')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {commissionTiers.map((tier) => (
                          <tr key={tier.name}>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900">{tier.name}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{tier.threshold}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{tier.commission}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{tier.perks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-sm text-gray-500">
                    {t('programDetails.table.note')}
                  </p>
                  <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
                    <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                      {t('programDetails.example.title')}
                    </p>
                    <p className="text-gray-700">
                      {t('programDetails.example.text')}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <h3 className="text-2xl font-semibold mb-4 flex items-center">
                    <GiftIcon className="w-8 h-8 text-blue-500 mr-3" />
                    {t('programDetails.cookieDuration.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('programDetails.cookieDuration.text')}
                  </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                >
                  <h3 className="text-2xl font-semibold mb-4 flex items-center">
                    <BanknotesIcon className="w-8 h-8 text-green-500 mr-3" />
                    {t('programDetails.payouts.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('programDetails.payouts.text')}
                  </p>
                </motion.div>

              </div>
            </div>
          </section>

          <section className="py-24 bg-gray-50 relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-12 w-72 h-72 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
              <div className="absolute bottom-10 right-12 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
              <AffiliateAmbassadorSection />
            </div>
          </section>

          {/* CTA */}
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white relative overflow-hidden"
              >
                <div className="relative z-10">
                  <h2 className="text-4xl font-bold mb-6">{t('cta.title')}</h2>
                  <p className="text-xl mb-8 max-w-2xl mx-auto">
                    {t('cta.subtitle')}
                  </p>
                  <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                  >
                    <Button
                        gray
                        onClick={() => window.open(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/invite/afivio`, '_blank')}
                    >
                      {t('cta.cta')}
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
          <SeoInternalLinks />
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  const { getI18nPropsWithSeo } = await import('@/utils/i18n');
  return getI18nPropsWithSeo(ctx, 'affiliateProgram', '/affiliate-program');
}
